import { useState } from "react";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Spinner from "react-bootstrap/Spinner";
import Alert from "react-bootstrap/Alert";
import ReportService from "../../services/report.service";
import { getSubsite, getUserCapabilities } from "../../reducers/auth";

const PrintItemOrdersReport = () => {
  const currentSubsite = useSelector(getSubsite);
  const userCapabilities = useSelector(getUserCapabilities);
  const { isSuperAdmin, loading } = useSelector(state => state.auth);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [allDates, setAllDates] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");

  const hasPrintItemReportCapability = userCapabilities?.some(
    c => c.capabilityName === "PRINT_ITEM_REPORT"
  );
  const canAccess = isSuperAdmin || hasPrintItemReportCapability;

  if (loading) {
    return (
      <Container className="text-center mt-5">
        <Spinner animation="border" />
      </Container>
    );
  }

  if (!canAccess) {
    return <Navigate to="/admin/home" replace />;
  }

  const downloadReport = async () => {
    setError("");
    if (!allDates && (!startDate || !endDate)) {
      setError("Please select a start and end date, or choose All Dates.");
      return;
    }
    if (!allDates && startDate > endDate) {
      setError("Start date must be on or before end date.");
      return;
    }

    setDownloading(true);
    try {
      const dates = allDates ? {} : { startDate, endDate };
      await ReportService.downloadPrintItemOrdersReport(dates, currentSubsite?.id);
    } catch (err) {
      console.error("Error downloading print item orders report:", err);
      setError("Error generating report. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Container className="mt-4" style={{ maxWidth: 720 }}>
      <h2 className="mb-2">Print Item Orders Report</h2>
      <p className="text-muted mb-4">
        Download an Excel report of print item orders
        {currentSubsite?.subsiteName ? ` for ${currentSubsite.subsiteName}` : ""}
        , including delivery and shipping/tracking details.
      </p>

      <div style={{ backgroundColor: "#f8f9fa", padding: 20, borderRadius: 8 }}>
        <Form.Check
          type="checkbox"
          id="printItemAllDates"
          className="mb-3"
          label="All Dates"
          checked={allDates}
          onChange={(e) => setAllDates(e.target.checked)}
        />

        <Row className="mb-3">
          <Col md={6}>
            <Form.Group controlId="printItemStartDate">
              <Form.Label>Start Date</Form.Label>
              <Form.Control
                type="date"
                value={startDate}
                disabled={allDates}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="printItemEndDate">
              <Form.Label>End Date</Form.Label>
              <Form.Control
                type="date"
                value={endDate}
                disabled={allDates}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </Form.Group>
          </Col>
        </Row>

        {error && <Alert variant="danger">{error}</Alert>}

        <Button
          variant="success"
          onClick={downloadReport}
          disabled={downloading}
        >
          {downloading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Generating…
            </>
          ) : (
            "Download Excel Report"
          )}
        </Button>
      </div>
    </Container>
  );
};

export default PrintItemOrdersReport;
